import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect, useCallback } from "react";

const SELECTED_ORG_KEY = "selectedOrganizationId";

export function useOrganization() {
  const queryClient = useQueryClient();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => {
    return localStorage.getItem(SELECTED_ORG_KEY);
  });

  // Fetch ALL organizations the user belongs to
  const { data: allOrganizations, isLoading: isLoadingAll } = useQuery({
    queryKey: ["user-organizations"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("organization_users")
        .select("organization_id, role, organizations(*)")
        .eq("user_id", user.id);

      if (error) throw error;
      return data || [];
    },
    retry: false,
  });

  // Determine the current organization
  const currentOrgData = allOrganizations?.find(
    (org) => org.organization_id === selectedOrgId
  ) || allOrganizations?.[0];

  // Auto-select the first org if none selected or selected org no longer exists
  useEffect(() => {
    if (allOrganizations && allOrganizations.length > 0) {
      const validSelection = allOrganizations.some(
        (org) => org.organization_id === selectedOrgId
      );
      if (!validSelection) {
        const firstOrgId = allOrganizations[0].organization_id;
        setSelectedOrgId(firstOrgId);
        localStorage.setItem(SELECTED_ORG_KEY, firstOrgId);
      }
    }
  }, [allOrganizations, selectedOrgId]);

  const switchOrganization = useCallback((orgId: string) => {
    setSelectedOrgId(orgId);
    localStorage.setItem(SELECTED_ORG_KEY, orgId);
    // Invalidate all queries to refetch data for the new organization
    queryClient.invalidateQueries();
  }, [queryClient]);

  return {
    // Current organization
    organization: currentOrgData?.organizations,
    organizationId: currentOrgData?.organization_id,
    role: currentOrgData?.role,
    isLoading: isLoadingAll,
    
    // All organizations for switching
    allOrganizations: allOrganizations?.map((org) => ({
      id: org.organization_id,
      name: org.organizations?.name || "Unknown",
      role: org.role,
    })) || [],
    
    // Switch function
    switchOrganization,
    
    // Check if user has multiple orgs
    hasMultipleOrganizations: (allOrganizations?.length || 0) > 1,
  };
}
